/**
 * The rows of the per-model tunables table that come from a deployment's
 * voice settings and that every Gemini Live model we carry takes the same way.
 *
 * Kept apart from `GeminiLiveModels.schema.ts` so each model entry spreads the
 * same object instead of repeating it; a model that differs overrides a key
 * after the spread.
 */

import {
  GEMINI_LIVE_ACTIVITY_HANDLINGS,
  type GeminiLiveTunables
} from "./GeminiLiveTunables.schema";

const GEMINI_LIVE_API_REFERENCE = "https://ai.google.dev/api/live";

/**
 * The deployment settings both models take the same way, from the Live API
 * reference (`RealtimeInputConfig`, `AutomaticActivityDetection`,
 * `AudioTranscriptionConfig`). A model that one day refuses one gets its own
 * row in its entry — overriding this spread — and the UI hides it there.
 *
 * `wire: "confirmed"` means v1beta ACCEPTED the field on both models in a
 * recording under apps/backend/src/voice-relay/infrastructure/adapters/gemini/__fixtures__/,
 * named beside each row. Whether it CHANGES what the model does is a separate
 * question, answered per row.
 */
export const LIVE_API_SETTING_TUNABLES = {
  // "If unspecified, the default behavior is START_OF_ACTIVITY_INTERRUPTS."
  // NO_INTERRUPTION accepted and honoured — a barge-in does not interrupt
  // (10-31/10-38-no-interruption); START_OF_ACTIVITY_INTERRUPTS sent
  // explicitly is accepted and a barge-in interrupts (16-31/16-38-activity-interrupts).
  activityHandling: {
    support: "configurable",
    values: GEMINI_LIVE_ACTIVITY_HANDLINGS,
    providerDefault: "START_OF_ACTIVITY_INTERRUPTS",
    doc: GEMINI_LIVE_API_REFERENCE,
    wire: "confirmed"
  },
  // Accepted in the builder's own frame, 600 ms: 14-31/14-38-production-setup.
  silenceDurationMs: { support: "configurable", doc: GEMINI_LIVE_API_REFERENCE, wire: "confirmed" },
  // Accepted in the builder's own frame, 40 ms: 14-31/14-38-production-setup.
  prefixPaddingMs: { support: "configurable", doc: GEMINI_LIVE_API_REFERENCE, wire: "confirmed" },
  // Accepted: 16-31/16-38-vocabulary. NO effect measured: the same jargon
  // sentence transcribes identically with and without it, on both models and
  // on both endpoints (16-*-vocabulary-control, 16-*-vocabulary-v1alpha).
  customVocabulary: { support: "configurable", doc: GEMINI_LIVE_API_REFERENCE, wire: "confirmed" },
  // Accepted regional, base and unlisted: 16-*-language-regional, -base,
  // -unlisted. A hint only: fr-FR over English speech leaves the English
  // transcript unchanged (16-*-language-mismatch).
  inputLanguageCodes: {
    support: "configurable",
    doc: GEMINI_LIVE_API_REFERENCE,
    wire: "confirmed"
  }
} as const satisfies Pick<
  GeminiLiveTunables,
  | "activityHandling"
  | "silenceDurationMs"
  | "prefixPaddingMs"
  | "customVocabulary"
  | "inputLanguageCodes"
>;
